import React, { useState, useEffect, useCallback } from 'react';
import {
  Globe, Users, DollarSign, TrendingUp, Link2, Clock,
  CheckCircle, XCircle, AlertCircle, Settings2, RefreshCw,
  ExternalLink, Copy, ArrowUpRight
} from 'lucide-react';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const statusColors = {
  pending_payment: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  paid: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  cancelled: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30',
};

const BrunoDashboard = () => {
  const [stats, setStats] = useState(null);
  const [deals, setDeals] = useState([]);
  const [partners, setPartners] = useState([]);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [webhookConfig, setWebhookConfig] = useState({ webhook_url: '', enabled: false, secret_key: '' });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('deals');

  const fetchData = useCallback(async () => {
    try {
      const [sRes, dRes, pRes, lRes, cRes] = await Promise.all([
        axios.get(`${API}/bruno/stats`),
        axios.get(`${API}/bruno/deals`),
        axios.get(`${API}/bruno/partners`),
        axios.get(`${API}/bruno/webhook-logs`),
        axios.get(`${API}/bruno/webhook-config`),
      ]);
      setStats(sRes.data);
      setDeals(dRes.data);
      setPartners(pRes.data);
      setWebhookLogs(lRes.data);
      setWebhookConfig(cRes.data);
    } catch (err) { console.warn('Bruno fetch error:', err?.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveWebhookConfig = async () => {
    try {
      await axios.put(`${API}/bruno/webhook-config`, webhookConfig);
      toast.success('Webhook configurado');
    } catch { toast.error('Error al guardar'); }
  };

  const copyEndpoint = () => {
    navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/bruno/leads/incoming`);
    toast.success('Endpoint copiado');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><RefreshCw className="w-6 h-6 animate-spin text-brand-deep dark:text-brand-deep dark:text-brand-green" /></div>;

  return (
    <div data-testid="bruno-dashboard" className="min-h-screen pb-24">
      <Header title="Bruno App" subtitle="Integracion hibrida - Leads, pagos y comisiones" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-6 mt-4">

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="bg-card/80 border-white/10">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-5 h-5 mx-auto mb-1 text-brand-deep dark:text-brand-green" />
                <p className="text-2xl font-bold">{stats.total_deals}</p>
                <p className="text-xs text-muted-foreground">Total Deals</p>
              </CardContent>
            </Card>
            <Card className="bg-card/80 border-white/10">
              <CardContent className="p-4 text-center">
                <DollarSign className="w-5 h-5 mx-auto mb-1 text-emerald-700 dark:text-emerald-400" />
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">${stats.total_revenue}</p>
                <p className="text-xs text-muted-foreground">Ingresos</p>
              </CardContent>
            </Card>
            <Card className="bg-card/80 border-white/10">
              <CardContent className="p-4 text-center">
                <Users className="w-5 h-5 mx-auto mb-1 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
                <p className="text-2xl font-bold">{stats.total_partners}</p>
                <p className="text-xs text-muted-foreground">Partners</p>
              </CardContent>
            </Card>
            <Card className="bg-card/80 border-white/10">
              <CardContent className="p-4 text-center">
                <CheckCircle className="w-5 h-5 mx-auto mb-1 text-brand-deep dark:text-brand-green" />
                <p className="text-2xl font-bold">{stats.conversion_rate}%</p>
                <p className="text-xs text-muted-foreground">Conversion</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* API Endpoint Info */}
        <Card className="bg-primary/10 border-violet-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Link2 className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
              <span className="text-sm font-semibold">Endpoint para BRUNO.APP</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-black/20 px-3 py-2 rounded-lg font-mono truncate">
                POST {process.env.REACT_APP_BACKEND_URL}/api/bruno/leads/incoming
              </code>
              <Button size="sm" variant="outline" onClick={copyEndpoint} className="gap-1 flex-shrink-0">
                <Copy className="w-3 h-3" /> Copiar
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              Datos: client, product, price, partner_id, referral_code, source, email, phone
            </p>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 bg-muted/30">
            <TabsTrigger value="deals">Deals ({deals.length})</TabsTrigger>
            <TabsTrigger value="partners">Partners ({partners.length})</TabsTrigger>
            <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
            <TabsTrigger value="config">Config</TabsTrigger>
          </TabsList>

          {/* Deals Tab */}
          <TabsContent value="deals" className="space-y-2 mt-4">
            {deals.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Globe className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Sin deals todavia</p>
                <p className="text-xs mt-1">Los leads de Bruno apareceran aqui</p>
              </div>
            ) : deals.map(deal => (
              <Card key={deal.id} className="bg-card/80 border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                        <span className="text-sm font-bold text-brand-deep dark:text-brand-green">{deal.client_name?.charAt(0) || '?'}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{deal.client_name}</p>
                        <p className="text-xs text-muted-foreground">{deal.product_service}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">${deal.price}</p>
                      <Badge className={cn("text-[10px]", statusColors[deal.status] || statusColors.pending_payment)}>
                        {deal.status === 'paid' ? 'Pagado' : deal.status === 'cancelled' ? 'Cancelado' : 'Pendiente'}
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Order: {deal.order_id}</span>
                    <span>Partner: {deal.partner_id?.slice(0, 12)}</span>
                    <span>Comision: ${deal.commission_amount}</span>
                    {deal.payment_link && (
                      <a href={deal.payment_link} target="_blank" rel="noreferrer" className="text-brand-deep dark:text-brand-deep dark:text-brand-green hover:text-violet-300 flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Link de pago
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners" className="space-y-2 mt-4">
            {partners.map(p => (
              <Card key={p.id || p.partner_id} className="bg-card/80 border-white/10">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{p.name || p.partner_id}</p>
                      <p className="text-xs text-muted-foreground">ID: {p.partner_id}</p>
                    </div>
                  </div>
                  <div className="text-right space-y-1">
                    <Badge variant="outline" className="text-[10px]">{(p.commission_rate * 100).toFixed(0)}% comision</Badge>
                    <p className="text-xs text-muted-foreground">{p.total_leads || 0} leads | {p.total_sales || 0} ventas</p>
                  </div>
                </CardContent>
              </Card>
            ))}
            {partners.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">Sin partners registrados</div>
            )}
          </TabsContent>

          {/* Webhooks Tab */}
          <TabsContent value="webhooks" className="space-y-2 mt-4">
            {webhookLogs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">Sin registros de webhooks</div>
            ) : webhookLogs.map((log, i) => (
              <Card key={log.id || `log-${i}`} className="bg-card/80 border-white/10">
                <CardContent className="p-3 flex items-center gap-3">
                  {log.success ? <CheckCircle className="w-4 h-4 text-emerald-700 dark:text-emerald-400 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">Deal: {log.deal_id?.slice(0, 12)}...</p>
                    <p className="text-[10px] text-muted-foreground">{log.sent_at?.slice(0, 19)} | {log.webhook_url}</p>
                  </div>
                  <Badge variant="outline" className={cn("text-[10px]", log.success ? "text-emerald-700 dark:text-emerald-400" : "text-red-600 dark:text-red-400")}>
                    {log.response_status || (log.success ? '200' : 'Error')}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Config Tab */}
          <TabsContent value="config" className="mt-4">
            <Card className="bg-card/80 border-white/10">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Settings2 className="w-5 h-5 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
                  <h3 className="font-semibold">Webhook de Salida a Bruno</h3>
                </div>
                <p className="text-xs text-muted-foreground">
                  Cuando un pago se confirma, DEAL enviara un webhook a esta URL con los datos de la transaccion.
                </p>
                <div>
                  <Label className="text-xs">URL del Webhook</Label>
                  <Input value={webhookConfig.webhook_url || ''} onChange={e => setWebhookConfig(c => ({ ...c, webhook_url: e.target.value }))}
                    placeholder="https://bruno.app/api/webhooks/deal" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Secret Key (firma HMAC)</Label>
                  <Input value={webhookConfig.secret_key || ''} onChange={e => setWebhookConfig(c => ({ ...c, secret_key: e.target.value }))}
                    placeholder="tu_secret_key" type="password" className="mt-1" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={webhookConfig.enabled} onChange={e => setWebhookConfig(c => ({ ...c, enabled: e.target.checked }))} className="rounded" />
                  <Label className="text-sm">Webhook activo</Label>
                </div>
                <Button onClick={saveWebhookConfig} className="bg-primary">
                  Guardar Configuracion
                </Button>

                <div className="mt-6 p-4 bg-muted/20 rounded-xl">
                  <p className="text-xs font-semibold mb-2">Payload que recibe Bruno:</p>
                  <pre className="text-[10px] text-muted-foreground bg-black/20 p-3 rounded-lg overflow-x-auto">
{JSON.stringify({
  deal_id: "uuid",
  order_id: "BRN-20260507-XXXXXXXX",
  payment_status: "completed",
  amount_paid: 250.00,
  partner_id: "BRUNO-P-001",
  commission_amount: 25.00,
  service_status: "active"
}, null, 2)}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default BrunoDashboard;
